import React, { useState, useEffect } from 'react';
import { getAllBanners, addBanner, updateBanner, deleteBanner } from './bannerService';
import { FiPlus, FiTrash2, FiEdit2, FiCheck, FiX, FiImage, FiExternalLink, FiMove } from 'react-icons/fi';
import './BannerManagement.css';

export default function BannerManagement() {
    const [banners, setBanners] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isAdding, setIsAdding] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [formData, setFormData] = useState({
        linkUrl: '',
        sequence: 0,
        active: true
    });
    const [selectedFile, setSelectedFile] = useState(null);
    const [previewUrl, setPreviewUrl] = useState(null);

    useEffect(() => {
        fetchBanners();
    }, []);

    async function fetchBanners() {
        try {
            const data = await getAllBanners();
            setBanners(data);
        } catch (error) {
            console.error('Error fetching banners:', error);
        } finally {
            setLoading(false);
        }
    }

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setSelectedFile(file);
            setPreviewUrl(URL.createObjectURL(file));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            if (editingId) {
                await updateBanner(editingId, formData, selectedFile);
            } else {
                await addBanner(formData, selectedFile);
            }
            setIsAdding(false);
            setEditingId(null);
            setFormData({ linkUrl: '', sequence: banners.length, active: true });
            setSelectedFile(null);
            setPreviewUrl(null);
            await fetchBanners();
        } catch (error) {
            console.error('Error saving banner:', error);
            alert('Failed to save banner');
        } finally {
            setLoading(false);
        }
    };

    const handleEdit = (banner) => {
        setEditingId(banner.id);
        setFormData({
            ...banner
        });
        setPreviewUrl(banner.imageUrl);
        setIsAdding(true);
    };

    const handleDelete = async (id, imageUrl) => {
        if (window.confirm('Are you sure you want to delete this banner?')) {
            setLoading(true);
            try {
                await deleteBanner(id, imageUrl);
                await fetchBanners();
            } catch (error) {
                console.error('Error deleting banner:', error);
            } finally {
                setLoading(false);
            }
        }
    };

    if (loading && banners.length === 0) return <div className="loading">Loading banners...</div>;

    return (
        <div className="page-container">
            <div className="page-header">
                <h1>Manage Ad Banners</h1>
                {!isAdding && (
                    <button className="add-button" onClick={() => setIsAdding(true)}>
                        <FiPlus /> Add New Banner
                    </button>
                )}
            </div>

            {isAdding && (
                <div className="modal-overlay">
                    <div className="modal-content banner-modal">
                        <div className="modal-header">
                            <h2>{editingId ? 'Edit Banner' : 'Add New Banner'}</h2>
                            <button type="button" className="modal-close" onClick={() => {
                                setIsAdding(false);
                                setEditingId(null);
                                setPreviewUrl(null);
                                setSelectedFile(null);
                            }}>
                                <FiX size={24} />
                            </button>
                        </div>
                        <form onSubmit={handleSubmit} className="banner-form">
                            <div className="form-section">
                                <label className="section-label">
                                    <FiImage size={18} />
                                    Banner Image
                                    <span className="label-hint">(Recommended: 1200x600px)</span>
                                </label>
                                <div className="image-upload-area">
                                    {previewUrl ? (
                                        <div className="banner-preview-card">
                                            <img src={previewUrl} alt="Preview" />
                                            <div className="preview-overlay">
                                                <button type="button" className="btn-remove-preview" onClick={() => {
                                                    setPreviewUrl(null);
                                                    setSelectedFile(null);
                                                }}>
                                                    <FiX size={20} />
                                                    Remove Image
                                                </button>
                                            </div>
                                        </div>
                                    ) : (
                                        <label className="file-dropzone">
                                            <div className="dropzone-content">
                                                <FiImage size={48} className="dropzone-icon" />
                                                <p className="dropzone-text">Click to upload image</p>
                                                <p className="dropzone-hint">or drag and drop</p>
                                                <p className="dropzone-formats">PNG, JPG up to 5MB</p>
                                            </div>
                                            <input type="file" onChange={handleFileChange} accept="image/*" required={!editingId} />
                                        </label>
                                    )}
                                </div>
                            </div>

                            <div className="form-row-split">
                                <div className="form-field">
                                    <label className="field-label">
                                        <FiMove size={16} />
                                        Sequence Order
                                    </label>
                                    <input
                                        type="number"
                                        className="field-input"
                                        value={formData.sequence}
                                        onChange={(e) => setFormData({ ...formData, sequence: parseInt(e.target.value) || 0 })}
                                        placeholder="0"
                                        required
                                    />
                                </div>
                                <div className="form-field">
                                    <label className="field-label">
                                        <FiCheck size={16} />
                                        Status
                                    </label>
                                    <select
                                        className="field-select"
                                        value={formData.active}
                                        onChange={(e) => setFormData({ ...formData, active: e.target.value === 'true' })}
                                    >
                                        <option value="true">✓ Active</option>
                                        <option value="false">✕ Inactive</option>
                                    </select>
                                </div>
                            </div>

                            <div className="form-field">
                                <label className="field-label">
                                    <FiExternalLink size={16} />
                                    Link URL
                                    <span className="label-optional">(Optional)</span>
                                </label>
                                <input
                                    type="url"
                                    className="field-input"
                                    placeholder="https://example.com"
                                    value={formData.linkUrl}
                                    onChange={(e) => setFormData({ ...formData, linkUrl: e.target.value })}
                                />
                            </div>

                            <div className="modal-footer">
                                <button type="button" className="btn-secondary" onClick={() => {
                                    setIsAdding(false);
                                    setEditingId(null);
                                    setPreviewUrl(null);
                                    setSelectedFile(null);
                                }}>
                                    <FiX size={18} />
                                    Cancel
                                </button>
                                <button type="submit" className="btn-primary" disabled={loading}>
                                    {loading ? (
                                        <>
                                            <span className="spinner"></span>
                                            Saving...
                                        </>
                                    ) : (
                                        <>
                                            <FiCheck size={18} />
                                            {editingId ? 'Update Banner' : 'Save Banner'}
                                        </>
                                    )}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            <div className="banners-grid">
                {banners.length === 0 ? (
                    <div className="empty-state">
                        <FiImage size={48} />
                        <p>No banners added yet.</p>
                    </div>
                ) : (
                    banners.map(banner => (
                        <div key={banner.id} className={`banner-card ${!banner.active ? 'inactive' : ''}`}>
                            <div className="banner-image-wrapper">
                                <img src={banner.imageUrl} alt="Banner" className="banner-img" />
                                {!banner.active && <div className="inactive-overlay">Inactive</div>}
                            </div>
                            <div className="banner-details">
                                <div className="banner-header">
                                    <div className="banner-meta-row">
                                        <span className="sequence-badge">
                                            <FiMove size={14} />
                                            Order {banner.sequence}
                                        </span>
                                        {banner.active ? (
                                            <span className="status-badge active">
                                                <FiCheck size={12} />
                                                Active
                                            </span>
                                        ) : (
                                            <span className="status-badge inactive">
                                                <FiX size={12} />
                                                Inactive
                                            </span>
                                        )}
                                    </div>
                                    {banner.linkUrl && (
                                        <a href={banner.linkUrl} target="_blank" rel="noopener noreferrer" className="banner-link">
                                            <FiExternalLink size={14} />
                                            View Link
                                        </a>
                                    )}
                                </div>
                                <div className="banner-action-buttons">
                                    <button onClick={() => handleEdit(banner)} className="btn-action btn-edit" title="Edit Banner">
                                        <FiEdit2 size={16} />
                                        Edit
                                    </button>
                                    <button onClick={() => handleDelete(banner.id, banner.imageUrl)} className="btn-action btn-delete" title="Delete Banner">
                                        <FiTrash2 size={16} />
                                        Delete
                                    </button>
                                </div>
                            </div>
                        </div>
                    ))
                )}
            </div>
        </div>
    );
}
